// Bavimail theme for Clerk's prebuilt components (<SignIn />, <UserProfile />).
// Applied once on <ClerkProvider>, so every Clerk surface shares it.
//
// Values are literal hex, not var(--x): Clerk derives its hover/border shades
// with color-mix() and recommends direct values for browser support. They mirror
// the hb-* tokens in src/styles.css; keep the two in sync.
//
// `bavimailClerkAppearance` (the provider default, so /login and every other
// surface) is the classic dark palette; its light mode is the html-level
// inversion filter in styles.css, which flips Clerk along with the rest of the
// interface (avatars and provider logos are counter-inverted there).
// Inside the dashboard, `createClerkAppearance` is called with the active
// palette so the account UI matches whichever theme the user picked.

import { dark } from "@clerk/ui/themes";
import type { ComponentProps } from "react";
import type { ClerkProvider } from "@clerk/tanstack-react-start";

type ClerkAppearance = NonNullable<ComponentProps<typeof ClerkProvider>["appearance"]>;

export type ClerkColors = Record<keyof typeof CLASSIC_COLORS, string>;

const CLASSIC_COLORS = {
  page: "#0d0d0f",
  inset: "#0a0a0c",
  surface: "#111114",
  raised: "#1a1a1e",
  border: "#1a1a1e",
  card: "#2a2a2e",
  borderHover: "#444444",
  text: "#d0d0d0",
  heading: "#ffffff",
  muted: "#8a8a8a",
  faint: "#7a7a7a",
  placeholder: "#6b6b6b",
  accent: "#f6821f",
  accentHover: "#ff8c2e",
  accentFg: "#000000",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
  ring: "rgba(246, 130, 31, 0.5)",
} as const;

const FONT_SANS = '"Manrope", system-ui, sans-serif';
const FONT_MONO = '"Chivo Mono", ui-monospace, monospace';

export type ClerkLayout =
  /** Clerk's own floating card: sign-in, and anywhere a profile opens as a modal. */
  | "card"
  /**
   * Fills its container and drops the card chrome, for a profile that is a page
   * of ours (Settings → Account). The container must have a height.
   */
  | "embedded";

export function createClerkAppearance(
  HB: ClerkColors,
  mode: "dark" | "light" = "dark",
  layout: ClerkLayout = "card",
): ClerkAppearance {
  // Mono, uppercase, tracked: the label voice used across the dashboard.
  const MONO_LABEL = {
    fontFamily: FONT_MONO,
    fontSize: "10px",
    fontWeight: 400,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: HB.faint,
  } as const;

  const FOCUS_RING = {
    outline: "none",
    boxShadow: `0 0 0 2px ${HB.page}, 0 0 0 4px ${HB.ring}`,
  } as const;

  // Matches Button variant="primary" shape="pill".
  const PRIMARY_BUTTON = {
    backgroundColor: HB.accent,
    color: HB.accentFg,
    fontFamily: FONT_SANS,
    fontSize: "14px",
    fontWeight: 700,
    textTransform: "none",
    letterSpacing: "0",
    borderRadius: "9999px",
    border: "none",
    boxShadow: "none",
    "&:hover": { backgroundColor: HB.accentHover },
    "&:active": { backgroundColor: HB.accentHover, transform: "scale(0.97)" },
    "&:focus-visible": FOCUS_RING,
  } as const;

  // Matches Button variant="outline": hairline border, no fill.
  const OUTLINE_BUTTON = {
    backgroundColor: "transparent",
    color: HB.text,
    fontFamily: FONT_SANS,
    fontWeight: 700,
    border: `1px solid ${HB.card}`,
    borderRadius: "9999px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "transparent", borderColor: HB.borderHover, color: HB.heading },
    "&:focus-visible": FOCUS_RING,
  } as const;

  // Matches Button variant="ghost": mono text action.
  const GHOST_ACTION = {
    fontFamily: FONT_MONO,
    fontSize: "11px",
    fontWeight: 400,
    color: HB.muted,
    borderRadius: "4px",
    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)", color: HB.heading },
    "&:focus-visible": FOCUS_RING,
  } as const;

  const DANGER_ACTION = {
    ...GHOST_ACTION,
    color: HB.danger,
    "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.1)", color: HB.danger },
  } as const;

  const appearance: ClerkAppearance = {
    // Clerk's dark base theme only suits dark palettes; light ones start from its default.
    ...(mode === "dark" ? { theme: dark } : {}),
    cssLayerName: "components",
    options: {
      logoPlacement: "inside",
      // Light mode swaps in /brandmark-light.svg from styles.css (.cl-logoImage).
      logoImageUrl: "/brandmark.svg",
      logoLinkUrl: "/",
      socialButtonsVariant: "blockButton",
      socialButtonsPlacement: "top",
      shimmer: false,
      termsPageUrl: "/terms",
      privacyPageUrl: "/privacy",
    },
    variables: {
      colorPrimary: HB.accent,
      colorPrimaryForeground: HB.accentFg,
      colorBackground: HB.surface,
      colorForeground: HB.text,
      colorMutedForeground: HB.muted,
      colorMuted: HB.inset,
      colorNeutral: HB.heading,
      colorInput: HB.inset,
      colorInputForeground: HB.heading,
      colorBorder: HB.card,
      colorRing: HB.ring,
      colorShadow: "#000000",
      colorShimmer: "rgba(255, 255, 255, 0.08)",
      colorModalBackdrop: "rgba(0, 0, 0, 0.7)",
      colorDanger: HB.danger,
      colorSuccess: HB.success,
      colorWarning: HB.warning,
      fontFamily: FONT_SANS,
      fontFamilyButtons: FONT_SANS,
      fontFamilyMono: FONT_MONO,
      fontSize: "0.8125rem",
      // DESIGN.md: sharp cards and controls; pills are set per button below.
      borderRadius: "0.25rem",
    },
    elements: {
      // Cards: flat hairline panels. Shadows are reserved for overlays.
      cardBox: {
        backgroundColor: HB.surface,
        border: `1px solid ${HB.card}`,
        borderRadius: "4px",
        boxShadow: "none",
      },
      card: { backgroundColor: HB.surface, boxShadow: "none", borderRadius: "0" },
      footer: {
        background: HB.inset,
        borderTop: `1px solid ${HB.border}`,
        "& > *": { background: "transparent" },
      },
      footerActionText: { color: HB.muted },
      footerActionLink: {
        color: HB.accent,
        fontWeight: 600,
        "&:hover": { color: HB.accentHover },
      },
      footerPagesLink: { color: HB.muted, "&:hover": { color: HB.heading } },

      headerTitle: {
        fontFamily: FONT_SANS,
        fontSize: "20px",
        fontWeight: 800,
        letterSpacing: "-0.02em",
        color: HB.heading,
      },
      headerSubtitle: { color: HB.muted },
      logoImage: { height: "32px", width: "auto" },

      socialButtonsBlockButton: { ...OUTLINE_BUTTON, height: "46px", fontSize: "14px" },
      socialButtonsBlockButtonText: { fontFamily: FONT_SANS, fontWeight: 700, fontSize: "14px" },
      socialButtonsIconButton: OUTLINE_BUTTON,
      dividerLine: { backgroundColor: HB.card },
      dividerText: MONO_LABEL,

      formFieldLabel: MONO_LABEL,
      formFieldInput: {
        backgroundColor: HB.inset,
        color: HB.heading,
        border: `1px solid ${HB.card}`,
        borderRadius: "4px",
        boxShadow: "none",
        "&::placeholder": { color: HB.placeholder },
        "&:hover": { borderColor: HB.borderHover, boxShadow: "none" },
        "&:focus": { borderColor: HB.accent, boxShadow: `0 0 0 1px ${HB.accent}` },
      },
      formFieldHintText: { color: HB.faint },
      formFieldErrorText: { color: HB.danger },
      formFieldSuccessText: { color: HB.success },
      otpCodeFieldInput: {
        backgroundColor: HB.inset,
        color: HB.heading,
        fontFamily: FONT_MONO,
        border: `1px solid ${HB.card}`,
        borderRadius: "4px",
        "&:focus": { borderColor: HB.accent, boxShadow: `0 0 0 1px ${HB.accent}` },
      },

      formButtonPrimary: { ...PRIMARY_BUTTON, height: "36px" },
      formButtonReset: GHOST_ACTION,
      button: { "&[data-variant='solid']::after": { display: "none" } },
      alternativeMethodsBlockButton: OUTLINE_BUTTON,
      backLink: GHOST_ACTION,
      identityPreview: {
        backgroundColor: HB.inset,
        border: `1px solid ${HB.card}`,
        borderRadius: "4px",
      },
      identityPreviewText: { color: HB.text },
      identityPreviewEditButton: { color: HB.accent },
      alert: {
        backgroundColor: HB.inset,
        border: `1px solid ${HB.card}`,
        borderRadius: "4px",
        color: HB.text,
      },

      // <UserProfile />
      navbar: {
        background: HB.inset,
        borderRight: `1px solid ${HB.border}`,
        "& h1": { fontFamily: FONT_SANS, fontWeight: 800, color: HB.heading },
        "& p": { color: HB.muted },
      },
      navbarButton: {
        fontFamily: FONT_SANS,
        fontWeight: 600,
        color: HB.muted,
        borderRadius: "4px",
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)", color: HB.heading },
        "&[data-active='true'], &.cl-active": {
          backgroundColor: HB.raised,
          color: HB.heading,
        },
        "&:focus-visible": FOCUS_RING,
      },
      navbarButtonIcon: { opacity: 1 },
      navbarMobileMenuRow: { background: HB.inset, borderBottom: `1px solid ${HB.border}` },
      scrollBox: { backgroundColor: HB.surface, borderRadius: "0", boxShadow: "none" },
      pageScrollBox: { backgroundColor: HB.surface },
      profilePage: { color: HB.text },
      profileSection: { borderTop: `1px solid ${HB.border}` },
      profileSectionTitleText: MONO_LABEL,
      profileSectionPrimaryButton: { ...GHOST_ACTION, color: HB.accent },
      profileSectionItem: { color: HB.text },
      userPreviewMainIdentifier: { color: HB.heading, fontWeight: 600 },
      userPreviewSecondaryIdentifier: { color: HB.muted },
      avatarBox: { borderRadius: "4px" },
      avatarImageActionsUpload: { ...GHOST_ACTION, color: HB.accent },
      avatarImageActionsRemove: DANGER_ACTION,
      badge: {
        fontFamily: FONT_MONO,
        fontSize: "10px",
        fontWeight: 400,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        borderRadius: "4px",
        border: `1px solid ${HB.card}`,
        backgroundColor: "transparent",
        color: HB.muted,
        boxShadow: "none",
      },
      accordionTriggerButton: { color: HB.text, "&:hover": { color: HB.heading } },
      activeDevice: { color: HB.text },
      tableHead: MONO_LABEL,

      // Overlays: the only place a shadow is allowed.
      menuList: {
        backgroundColor: HB.raised,
        border: `1px solid ${HB.card}`,
        borderRadius: "4px",
        boxShadow: "0 16px 40px rgba(0, 0, 0, 0.5)",
      },
      menuItem: {
        color: HB.text,
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)", color: HB.heading },
      },
      menuItem__danger: { color: HB.danger },
      actionCard: {
        backgroundColor: HB.raised,
        border: `1px solid ${HB.card}`,
        borderRadius: "4px",
        boxShadow: "none",
      },
      modalBackdrop: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
      modalContent: { boxShadow: "0 24px 64px rgba(0, 0, 0, 0.6)" },
      modalCloseButton: GHOST_ACTION,
    },
  };

  if (layout === "embedded") {
    // Clerk sizes the profile as a 55rem x 44rem card. Here it is the page, so it
    // takes the panel's size and the panel's background instead of a raised one.
    const fill = { width: "100%", maxWidth: "100%", height: "100%" } as const;
    appearance.elements = {
      ...appearance.elements,
      rootBox: fill,
      cardBox: {
        ...fill,
        backgroundColor: HB.page,
        border: "none",
        borderRadius: "0",
        boxShadow: "none",
      },
      card: { backgroundColor: HB.page, boxShadow: "none", borderRadius: "0" },
      scrollBox: { backgroundColor: HB.page, borderRadius: "0", boxShadow: "none" },
      pageScrollBox: { backgroundColor: HB.page },
      navbar: {
        background: HB.page,
        borderRight: `1px solid ${HB.border}`,
        // "Account / Manage your account info." repeats the Settings header above it.
        "& h1, & p": { display: "none" },
      },
      navbarMobileMenuRow: { background: HB.page, borderBottom: `1px solid ${HB.border}` },
      // Rows are label-left, action-right: past this width they fall apart.
      profilePage: { color: HB.text, maxWidth: "46rem" },
    };
  }

  return appearance;
}

export const bavimailClerkAppearance = createClerkAppearance(CLASSIC_COLORS);
